<template>
  <div id="ingredients" class="md:h-full bg-primary px-4 md:px-8 lg:p-16 py-8">
    <!-- Title -->
    <h2 class="ltr:font-ltr-heading  text-h2 mb-8 text-white">
      {{ title }}
    </h2>

    <!-- Portion selector -->
    <div
      v-if="portions"
      class="flex bg-white mb-6"
    >
      <button
        v-if="ingredients.amountIsScalable"
        class="w-12 h-12 ltr:border-r-2 rtl:border-l-2 border-beige"
        data-gtm-trigger="click"
        @click="performStep('down')"
      >
        <IconMinus
          class="icon m-auto"
          width="16px"
        />
      </button>
      <div class="flex-1 flex items-center h-12">
        <span class="text-xs mx-auto">{{ portions }} {{ amountText }}</span>
      </div>
      <button
        v-if="ingredients.amountIsScalable"
        class="w-12 h-12 ltr:border-l-2 rtl:border-r-2 border-beige"
        data-gtm-trigger="click"
        @click="performStep('up')"
      >
        <IconPlus
          class="icon m-auto"
          width="16px"
        />
      </button>
    </div>

    <!-- Ingredient list -->
    <div v-for="ingredientGroup in scaledIngredientsGroups" :key="ingredientGroup.index">
      <h3 v-if="ingredientGroup.name" class="text-2xl mb-4 text-white">
        {{ ingredientGroup.name }}
      </h3>

      <ul class="text-xs bg-white mb-10">
        <li v-for="ingredient in ingredientGroup.items"
            :key="ingredient.id"
            class="flex items-center p-4 border-t-2 border-beige first:border-t-0"
        >
          <input :id="ingredientGroup.index + ingredient.name.value.ingredientName"
                 :name="ingredientGroup.name"
                 type="checkbox"
                 @change="trackIngredientTick(ingredient.name.value.ingredientName, $event.target.checked)"
          >
          <label :for="ingredientGroup.index + ingredient.name.value.ingredientName" class="flex-1 flex items-center justify-between">
            <div class="flex items-center">
              <a v-if="ingredient.product"
                 :href="ingredient.product.url"
                 class="flex items-center hover:underline"
              >
                <Picture
                  v-bind="ingredient.product.image"
                  img-class="h-12 mr-2"
                  class="flex-shrink-0"
                  width="48"
                  height="48"
                  lazy
                />
                <span class="font-bold leading-5">{{ capitilize(ingredient.name.value.ingredientName) }}</span>
              </a>
              <span v-else class="font-bold">{{ capitilize(ingredient.name.value.ingredientName) }}</span>
            </div>
            <div class="whitespace-no-wrap opacity-75 ltr:ml-4 rtl:mr-4">{{ ingredient.name.value.ingredientUnitName }}
              <span v-if="ingredient.secondaryUnit.value">({{ ingredient.secondaryUnit.value }})</span>
            </div>
          </label>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import IconMinus from '@/assets/icons/minus.svg'
import IconPlus from '@/assets/icons/plus.svg'
import ingredientScaleHelper from '@/compositions/recipes/RecipeIngrediensScaleHelper'
import Picture from '@/components/Picture.vue'
import { computed } from 'vue'

export default {
  components: {
    IconMinus,
    IconPlus,
    Picture
  },
  props: {
    ingredients: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    language: {
      type: String,
      default: ''
    },
    portionSingularText: {
      type: String,
      default: 'portion'
    },
    portionPluralText: {
      type: String,
      default: 'portions'
    }
  },
  setup(props) {
    const { setPortions, state } = ingredientScaleHelper(props.ingredients, 'en', props.portionSingularText, props.portionPluralText)

    const minValue = 1
    const maxValue = state.basePortions * 10
    const stepValue = 1

    function performStep(action) {
      let newValue
      if (action === 'up') {
        newValue = Math.floor((state.portions + stepValue) / stepValue) * stepValue
      } else {
        newValue = Math.ceil((state.portions - stepValue) / stepValue) * stepValue
      }

      if (action === 'up' && newValue <= maxValue) {
        setPortions(Math.floor((state.portions + stepValue) / stepValue) * stepValue)
      } else if (action === 'down' && newValue >= minValue) {
        setPortions(Math.ceil((state.portions - stepValue) / stepValue) * stepValue)
      }
    }

    function capitilize(name) {
      return name.charAt(0).toUpperCase() + name.slice(1)
    }

    function trackIngredientTick(name, checked) {
      if (checked) {
        window.dataLayer.push({
          event: 'checkIngredient',
          ingredientName: name
        })
      }
    }

    return {
      minValue,
      maxValue,
      stepValue,
      portions: computed(() => state.portions),

      amountText: computed(() => state.amountText),
      performStep,
      scaledIngredientsGroups: computed(() => state.ingredientGroups),

      capitilize,
      trackIngredientTick
    }
  }
}
</script>
